import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
    Eye,
    UsersThree,
    Pulse,
    Globe,
    DeviceMobile,
    Desktop,
    DeviceTablet,
    ArrowLeft,
    ArrowClockwise,
} from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value, g }) {
    return (
        <div
            className={`relative overflow-hidden ${g} p-5 text-white shadow-lg`}
        >
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
            <div className="flex items-center justify-between relative">
                <div className="text-xs uppercase tracking-widest text-white/80">
                    {label}
                </div>
                <Icon size={20} weight="duotone" className="text-white/90" />
            </div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2 relative">
                {value}
            </div>
        </div>
    );
}

function DailyBars({ data }) {
    const max = Math.max(1, ...data.map((d) => Number(d.visits) || 0));
    return (
        <div className="flex items-end gap-1 h-32" data-testid="visitor-bars">
            {data.map((d) => {
                const v = Number(d.visits) || 0;
                const h = Math.max(2, Math.round((v / max) * 128));
                return (
                    <div
                        key={d.day}
                        className="flex-1 flex flex-col items-center gap-1 group"
                        title={`${d.day}: ${v} visits · ${d.unique_visitors} unique`}
                    >
                        <div className="text-[10px] text-zinc-500 font-mono opacity-0 group-hover:opacity-100 transition">
                            {v}
                        </div>
                        <div
                            className="w-full bg-violet-500/80 group-hover:bg-violet-700 transition-colors"
                            style={{ height: `${h}px` }}
                        />
                        <div className="text-[10px] text-zinc-400 font-mono">
                            {String(d.day).slice(5)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function MonthlyChart({ data }) {
    const W = 800;
    const H = 240;
    const PAD_L = 40;
    const PAD_R = 16;
    const PAD_T = 16;
    const PAD_B = 32;
    const innerW = W - PAD_L - PAD_R;
    const innerH = H - PAD_T - PAD_B;

    const visits = data.map((d) => Number(d.visits) || 0);
    const uniques = data.map((d) => Number(d.unique_visitors) || 0);
    const max = Math.max(1, ...visits, ...uniques);
    const ticks = 4;

    const x = (i) =>
        PAD_L +
        (data.length === 1 ? innerW / 2 : (i * innerW) / (data.length - 1));
    const y = (v) => PAD_T + innerH - (v / max) * innerH;

    const linePath = (arr) =>
        arr.map((v, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(v)}`).join(" ");
    const areaPath =
        `M${x(0)},${y(visits[0] || 0)} ` +
        visits.map((v, i) => `L${x(i)},${y(v)}`).join(" ") +
        ` L${x(visits.length - 1)},${PAD_T + innerH} L${x(0)},${PAD_T + innerH} Z`;

    const totalVisits = visits.reduce((a, b) => a + b, 0);
    const totalUnique = uniques.reduce((a, b) => a + b, 0);

    return (
        <div data-testid="visitor-monthly-chart">
            <div className="flex flex-wrap items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 bg-violet-500" />
                    <span className="text-zinc-600">Visits</span>
                    <span className="font-mono font-bold text-zinc-900">
                        {totalVisits}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 bg-pink-500" />
                    <span className="text-zinc-600">Unique</span>
                    <span className="font-mono font-bold text-zinc-900">
                        {totalUnique}
                    </span>
                </div>
            </div>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                className="w-full h-64"
                preserveAspectRatio="none"
            >
                <defs>
                    <linearGradient id="vfill" x1="0" x2="0" y1="0" y2="1">
                        <stop
                            offset="0%"
                            stopColor="#8b5cf6"
                            stopOpacity="0.35"
                        />
                        <stop
                            offset="100%"
                            stopColor="#8b5cf6"
                            stopOpacity="0"
                        />
                    </linearGradient>
                </defs>
                {/* Y-axis grid lines + labels */}
                {Array.from({ length: ticks + 1 }).map((_, i) => {
                    const val = Math.round((max * (ticks - i)) / ticks);
                    const yy = PAD_T + (innerH * i) / ticks;
                    return (
                        <g key={i}>
                            <line
                                x1={PAD_L}
                                x2={W - PAD_R}
                                y1={yy}
                                y2={yy}
                                stroke="#e4e4e7"
                                strokeDasharray="2 3"
                            />
                            <text
                                x={PAD_L - 6}
                                y={yy + 3}
                                textAnchor="end"
                                fontSize="10"
                                fill="#a1a1aa"
                                fontFamily="monospace"
                            >
                                {val}
                            </text>
                        </g>
                    );
                })}
                {/* X-axis labels (every 5th day) */}
                {data.map((d, i) =>
                    i % 5 === 0 || i === data.length - 1 ? (
                        <text
                            key={d.day}
                            x={x(i)}
                            y={H - 10}
                            textAnchor="middle"
                            fontSize="10"
                            fill="#71717a"
                            fontFamily="monospace"
                        >
                            {String(d.day).slice(5)}
                        </text>
                    ) : null,
                )}
                {/* Area + lines */}
                <path d={areaPath} fill="url(#vfill)" />
                <path
                    d={linePath(visits)}
                    fill="none"
                    stroke="#8b5cf6"
                    strokeWidth="2"
                />
                <path
                    d={linePath(uniques)}
                    fill="none"
                    stroke="#ec4899"
                    strokeWidth="2"
                    strokeDasharray="4 3"
                />
                {/* Points with tooltips */}
                {data.map((d, i) => (
                    <g key={d.day}>
                        <circle
                            cx={x(i)}
                            cy={y(visits[i])}
                            r="3"
                            fill="#8b5cf6"
                        >
                            <title>{`${d.day}: ${d.visits} visits · ${d.unique_visitors} unique`}</title>
                        </circle>
                    </g>
                ))}
            </svg>
        </div>
    );
}

const DEVICE_ICON = {
    mobile: DeviceMobile,
    tablet: DeviceTablet,
    desktop: Desktop,
};

export default function AdminVisitors() {
    const [vis, setVis] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/admin/visitors")
            .then(({ data }) => setVis(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30000); // auto-refresh every 30s
        return () => clearInterval(id);
    }, []);

    const totalDeviceVisits =
        (vis?.devices || []).reduce((s, d) => s + Number(d.visits || 0), 0) ||
        1;

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10"
            data-testid="admin-visitors-page"
        >
            {/* Header */}
            <div className="relative overflow-hidden mb-8 p-8 bg-mesh-dark text-white shadow-2xl">
                <div className="absolute top-0 right-0 w-96 h-96 g-violet opacity-30 rounded-full blur-3xl" />
                <div className="relative flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <Link
                            to="/admin"
                            className="text-xs font-mono uppercase tracking-widest text-violet-300 inline-flex items-center gap-1 hover:text-violet-200"
                        >
                            <ArrowLeft size={12} /> Admin
                        </Link>
                        <h1 className="font-display font-black text-5xl tracking-tighter mt-1">
                            Visitor{" "}
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-pink-400 to-amber-400">
                                analytics.
                            </span>
                        </h1>
                        <p className="text-white/70 text-sm mt-2 max-w-md">
                            Real-time tracking of every page visit on
                            idcardgen.com.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {vis?.live_now != null && (
                            <div className="flex items-center gap-2 text-sm bg-white/10 border border-white/20 px-3 py-2 backdrop-blur">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                                </span>
                                <span className="font-mono">
                                    {vis.live_now} live now
                                </span>
                            </div>
                        )}
                        <button
                            onClick={load}
                            className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 px-4 py-2 text-sm flex items-center gap-2"
                            data-testid="visitors-refresh"
                        >
                            <ArrowClockwise
                                size={14}
                                className={loading ? "animate-spin" : ""}
                            />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Stat grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Stat
                    icon={Eye}
                    label="Total Visits"
                    value={vis?.total_visits ?? "—"}
                    g="g-violet"
                />
                <Stat
                    icon={UsersThree}
                    label="Unique Visitors"
                    value={vis?.unique_visitors ?? "—"}
                    g="g-ocean"
                />
                <Stat
                    icon={Pulse}
                    label="Today"
                    value={`${vis?.today_visits ?? 0} / ${vis?.today_unique ?? 0}u`}
                    g="g-emerald"
                />
                <Stat
                    icon={Globe}
                    label="Last 30 days"
                    value={`${vis?.month_visits ?? 0} / ${vis?.month_unique ?? 0}u`}
                    g="g-sunset"
                />
            </div>

            {/* Range breakdown */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <div className="card-surface p-5">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">
                        Today
                    </div>
                    <div className="font-display font-black text-3xl mt-1">
                        {vis?.today_visits ?? "—"}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        {vis?.today_unique ?? 0} unique
                    </div>
                </div>
                <div className="card-surface p-5">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">
                        Last 7 days
                    </div>
                    <div className="font-display font-black text-3xl mt-1">
                        {vis?.week_visits ?? "—"}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        {vis?.week_unique ?? 0} unique
                    </div>
                </div>
                <div className="card-surface p-5">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">
                        Last 30 days
                    </div>
                    <div className="font-display font-black text-3xl mt-1">
                        {vis?.month_visits ?? "—"}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        {vis?.month_unique ?? 0} unique
                    </div>
                </div>
            </div>

            {/* Monthly line chart (30 days) */}
            {vis?.monthly?.length > 0 && (
                <div className="card-surface p-6 mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="font-display font-bold text-lg">
                            Last 30 days
                        </h2>
                        <span className="text-xs text-zinc-500 font-mono">
                            visits & unique visitors
                        </span>
                    </div>
                    <MonthlyChart data={vis.monthly} />
                </div>
            )}

            {/* Daily bars */}
            {vis?.daily?.length > 0 && (
                <div className="card-surface p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-bold text-lg">
                            Last 14 days
                        </h2>
                        <span className="text-xs text-zinc-500 font-mono">
                            visits per day
                        </span>
                    </div>
                    <DailyBars data={vis.daily} />
                </div>
            )}

            {/* Top pages + referrers + devices */}
            <div className="grid lg:grid-cols-3 gap-4">
                <div className="card-surface p-6 lg:col-span-2">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">
                        Top pages (30 days)
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase text-zinc-400 border-b">
                                    <th className="py-2">Path</th>
                                    <th className="py-2 text-right">Visits</th>
                                    <th className="py-2 text-right">Unique</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {(vis?.top_pages || []).map((p) => (
                                    <tr key={p.path}>
                                        <td className="py-2 font-mono truncate max-w-xs">
                                            {p.path}
                                        </td>
                                        <td className="py-2 text-right font-semibold">
                                            {p.visits}
                                        </td>
                                        <td className="py-2 text-right text-zinc-500">
                                            {p.unique_visitors}
                                        </td>
                                    </tr>
                                ))}
                                {!vis?.top_pages?.length && (
                                    <tr>
                                        <td
                                            colSpan={3}
                                            className="py-4 text-center text-zinc-400"
                                        >
                                            No data yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="card-surface p-6">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">
                        Devices (30 days)
                    </div>
                    <ul className="space-y-3">
                        {(vis?.devices || []).map((d) => {
                            const Icon = DEVICE_ICON[d.device] || Globe;
                            const pct = Math.round(
                                (Number(d.visits) / totalDeviceVisits) * 100,
                            );
                            return (
                                <li key={d.device}>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="flex items-center gap-2 capitalize">
                                            <Icon size={16} weight="duotone" />
                                            {d.device}
                                        </span>
                                        <span className="text-zinc-500 text-xs">
                                            {d.visits} · {pct}%
                                        </span>
                                    </div>
                                    <div className="h-1.5 bg-zinc-100 mt-1 overflow-hidden">
                                        <div
                                            className="h-full g-violet"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                        {!vis?.devices?.length && (
                            <li className="text-zinc-400 text-sm">
                                No data yet.
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Referrers */}
            <div className="card-surface p-6 mt-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">
                    Top referrers (30 days)
                </div>
                <ul className="text-sm divide-y divide-zinc-100">
                    {(vis?.top_referrers || []).map((r, i) => (
                        <li
                            key={i}
                            className="py-2 flex items-center justify-between gap-3"
                        >
                            <a
                                href={r.referrer}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="truncate text-zinc-700 hover:text-violet-700 underline-offset-2 hover:underline"
                            >
                                {r.referrer}
                            </a>
                            <span className="text-zinc-500 text-xs whitespace-nowrap font-mono">
                                {r.visits}
                            </span>
                        </li>
                    ))}
                    {!vis?.top_referrers?.length && (
                        <li className="py-2 text-zinc-400">
                            No referrers yet — direct traffic only.
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
}
